import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { AI_MODELS, streamSSE } from "@/lib/stream";
import { ChatText } from "@/components/ChatText";

const sessionId = () => {
  let id = localStorage.getItem("abs_chat_id");
  if (!id) { id = `chat_${crypto.randomUUID().replace(/-/g, "")}`; localStorage.setItem("abs_chat_id", id); }
  return id;
};

const SUGGESTIONS = ["What should I wear for leg day?", "Best gift under $40?", "Which bottle keeps water coldest?"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("claude");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (open) api.get(`/chat/${sessionId()}`).then((r) => setMessages(r.data)); }, [open]);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (d) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c.at(-1), content: c.at(-1).content + d }; return c; });
    try {
      await streamSSE("/chat/stream", { session_id: sessionId(), message: msg, model }, append);
    } catch (e) {
      append(e.message);
    } finally { setBusy(false); }
  };

  if (!open) return (
    <button data-testid="chat-open-button" onClick={() => setOpen(true)}
      className="fixed bottom-6 left-6 z-40 h-14 pl-4 pr-5 bg-white text-black hover:bg-[#FF3B30] hover:text-white flex items-center gap-2 font-display uppercase font-black tracking-wider shadow-2xl transition-colors">
      <MessageCircle className="w-5 h-5" /> Ask ABS Coach
    </button>
  );

  return (
    <div data-testid="chat-panel" className="fixed bottom-6 left-4 right-4 sm:left-6 sm:right-auto z-40 sm:w-[380px] h-[560px] max-h-[80vh] flex flex-col bg-[#0d0d10] border border-white/15 shadow-2xl rise">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div>
          <p className="font-display uppercase font-black text-xl leading-none">ABS Coach</p>
          <p className="font-mono text-[10px] text-neutral-500 tracking-widest mt-1">AI GEAR ADVISOR</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="chat-model-select" value={model} onChange={(e) => setModel(e.target.value)}
            className="bg-white/5 border border-white/15 h-8 px-2 text-xs font-mono uppercase">
            {AI_MODELS.map((m) => <option key={m.id} value={m.id} className="bg-black">{m.label}</option>)}
          </select>
          <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="w-8 h-8 grid place-items-center hover:bg-white/10 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-neutral-400">Tell me your training style and I'll match you with ABS gear.</p>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="block w-full text-left text-sm px-3 py-2 border border-white/10 hover:border-[#FF3B30] hover:text-white text-neutral-300 transition-colors">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-message-${m.role}`} className={`text-sm leading-relaxed whitespace-pre-wrap max-w-[88%] px-3 py-2 ${m.role === "user" ? "ml-auto bg-[#FF3B30] text-white" : "bg-white/5 text-neutral-200"}`}>
            {m.content ? <ChatText text={m.content} onNavigate={() => setOpen(false)} /> : <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex gap-2">
        <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about gear, sizes, gifts..."
          className="flex-1 h-10 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm" />
        <button data-testid="chat-send-button" disabled={busy} className="w-10 h-10 grid place-items-center bg-[#FF3B30] hover:bg-[#E02E24] text-white disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};
